export type UnknownRecord = Record<string, unknown>;

const dtoAnomalies = new Set<string>();

export function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function asRecord(value: unknown): UnknownRecord | null {
  return isRecord(value) ? value : null;
}

export function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function asOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
}

export function asBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }
  }

  return fallback;
}

export function asArray<T>(
  value: unknown,
  mapItem: (item: unknown, index: number) => T,
): T[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(mapItem);
}

export function asOptionalArray<T>(
  value: unknown,
  mapItem: (item: unknown, index: number) => T,
): T[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.map(mapItem);
}

export function reportDtoAnomaly(entity: string, field: string, raw: unknown): void {
  if (!shouldReportDtoAnomaly()) {
    return;
  }

  const preview = formatAnomalyPreview(raw);
  const key = `${entity}:${field}:${preview}`;

  if (dtoAnomalies.has(key)) {
    return;
  }

  dtoAnomalies.add(key);
  console.warn(`[dto] ${entity}.${field}`, raw);
}

function shouldReportDtoAnomaly(): boolean {
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === 'test.flowship.work';
}

function formatAnomalyPreview(raw: unknown): string {
  if (typeof raw === 'string') {
    return raw.slice(0, 120);
  }

  try {
    return JSON.stringify(raw)?.slice(0, 120) || 'unknown';
  } catch {
    return 'unserializable';
  }
}

/** Explicit unknown financial values remain unknown; absent legacy values retain the existing default. */
export function asNullableNumber(value: unknown): number | null {
  return value === null ? null : asNumber(value);
}
